// frontend/src/components/Auth/LoginModal.tsx
import { useState, useEffect, type FormEvent } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Mail, AlertCircle, KeyRound, ArrowLeft } from 'lucide-react';
import BaseModal from '../shared/modals/BaseModal';
import { Button, Input, FormField } from '../ui';
import { hasPlusTag, PLUS_ADDRESS_MESSAGE } from '../../utils/emailPolicy';
import TurnstileWidget, { TURNSTILE_SITE_KEY } from './TurnstileWidget';

export interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Turnstile site key; '' turns the widget off. Defaults to the build's
   *  REACT_APP_TURNSTILE_SITE_KEY (tests pass one explicitly). */
  captchaSiteKey?: string;
}

/** Google's multi-color "G" mark (lucide has no brand icons). */
const GoogleIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"
    />
  </svg>
);

type Mode = 'email' | 'code';

const LoginModal = ({ isOpen, onClose, captchaSiteKey = TURNSTILE_SITE_KEY }: LoginModalProps) => {
  const { signInWithGoogle, sendEmailOtp, verifyEmailOtp, error: authError, clearError } = useAuth();
  const [mode, setMode] = useState<Mode>('email');
  const [email, setEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [googleLoading, setGoogleLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [error, setError] = useState('');
  // Turnstile (open-access plan §3.7): every request that emails a code needs a
  // fresh token. A token is single-use, so bump captchaReset after each one.
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaReset, setCaptchaReset] = useState(0);
  const needsCaptcha = Boolean(captchaSiteKey);
  const captchaMissing = needsCaptcha && !captchaToken;

  // Reset transient state each time the modal opens.
  useEffect(() => {
    if (isOpen) {
      setMode('email');
      setEmail('');
      setOtpCode('');
      setError('');
      setGoogleLoading(false);
      setOtpLoading(false);
      if (clearError) clearError();
    }
  }, [isOpen, clearError]);

  const handleGoogle = async () => {
    setGoogleLoading(true);
    setError('');
    const { error: googleError } = await signInWithGoogle();
    // On success the browser redirects to Google; only surface failures.
    if (googleError) {
      setError(googleError.message);
      setGoogleLoading(false);
    }
  };

  const handleSendOtp = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Refuse +tag addresses before Supabase mints an identity and sends a code.
    if (hasPlusTag(email)) {
      setError(PLUS_ADDRESS_MESSAGE);
      return;
    }
    if (captchaMissing) {
      setError('Please complete the verification check.');
      return;
    }
    setOtpLoading(true);
    setError('');
    const { error: otpError } = await sendEmailOtp(email.trim(), captchaToken ?? undefined);
    setOtpLoading(false);
    setCaptchaReset((n) => n + 1);
    if (otpError) {
      setError(otpError.message);
    } else {
      setOtpCode('');
      setMode('code');
    }
  };

  const handleResendOtp = async () => {
    if (captchaMissing) {
      setError('Please complete the verification check.');
      return;
    }
    setOtpLoading(true);
    setError('');
    const { error: otpError } = await sendEmailOtp(email.trim(), captchaToken ?? undefined);
    setOtpLoading(false);
    setCaptchaReset((n) => n + 1);
    if (otpError) setError(otpError.message);
  };

  const handleVerifyOtp = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setOtpLoading(true);
    setError('');
    const { error: verifyError } = await verifyEmailOtp(email.trim(), otpCode.trim());
    // On success the auth state change unmounts this auth UI; only surface failures.
    if (verifyError) {
      const msg = verifyError.message.includes('expired') || verifyError.message.includes('invalid')
        ? 'That code is invalid or expired. Request a new one.'
        : verifyError.message;
      setError(msg);
      setOtpLoading(false);
    }
  };

  const message = error || authError;

  const ErrorBox = message ? (
    <div
      role="alert"
      className="flex items-start gap-2 rounded-control bg-status-critical/10 p-3 text-sm text-status-critical-fg"
    >
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-status-critical" />
      <span>{message}</span>
    </div>
  ) : null;

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="Sign in"
      subtitle="Continue to your planner"
      maxWidth="max-w-md"
    >
      <div className="px-6 py-6">
        {mode === 'code' ? (
          /* Step 2: enter the emailed code. */
          <form onSubmit={handleVerifyOtp} className="space-y-4">
            <button
              type="button"
              onClick={() => {
                setError('');
                setOtpCode('');
                setMode('email');
              }}
              className="inline-flex items-center gap-1 text-sm font-medium text-ink-2 hover:text-primary"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>

            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-control bg-primary/10 text-primary">
                <KeyRound className="h-5 w-5" />
              </span>
              <div>
                <h2 className="text-base font-semibold text-ink">Enter your code</h2>
                <p className="text-sm text-ink-2">
                  We emailed a sign-in code to <span className="font-medium text-ink">{email}</span>.
                </p>
              </div>
            </div>

            <FormField label="Sign-in code">
              {(field) => (
                <Input
                  {...field}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                  placeholder="Enter the code from your email"
                  maxLength={8}
                  required
                  className="text-center font-mono text-lg tracking-[0.3em]"
                />
              )}
            </FormField>

            {ErrorBox}

            <Button
              type="submit"
              isLoading={otpLoading}
              disabled={otpLoading || otpCode.length < 6}
              className="w-full"
            >
              {otpLoading ? 'Verifying…' : 'Verify & sign in'}
            </Button>

            <TurnstileWidget siteKey={captchaSiteKey} onToken={setCaptchaToken} resetKey={captchaReset} />

            <div className="text-center text-sm text-ink-2">
              Didn&rsquo;t get it?{' '}
              <button
                type="button"
                onClick={handleResendOtp}
                disabled={otpLoading || captchaMissing}
                className="font-medium text-primary hover:underline disabled:opacity-60"
              >
                Resend code
              </button>
            </div>
          </form>
        ) : (
          /* Step 1: Google, or request an email OTP. */
          <>
            <button
              type="button"
              onClick={handleGoogle}
              disabled={googleLoading || otpLoading}
              className="flex w-full items-center justify-center gap-2.5 rounded-control border border-line-soft bg-surface-card px-4 py-2.5 text-sm font-medium text-ink transition-colors duration-fast hover:bg-surface-sunken focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-60"
            >
              <GoogleIcon className="h-4 w-4" />
              {googleLoading ? 'Redirecting…' : 'Continue with Google'}
            </button>

            <div className="my-4 flex items-center gap-3 text-xs text-ink-3">
              <span className="h-px flex-1 bg-line-soft" />
              or
              <span className="h-px flex-1 bg-line-soft" />
            </div>

            <form onSubmit={handleSendOtp} className="space-y-4">
              <FormField label="Email address" description="We'll email you a sign-in code — no password needed.">
                {(field) => (
                  <div className="relative">
                    <Mail className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-3" />
                    <Input
                      {...field}
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@university.edu"
                      className="pl-9"
                    />
                  </div>
                )}
              </FormField>

              <TurnstileWidget siteKey={captchaSiteKey} onToken={setCaptchaToken} resetKey={captchaReset} />

              {ErrorBox}

              <Button type="submit" isLoading={otpLoading} disabled={captchaMissing} className="w-full">
                {otpLoading ? 'Sending…' : 'Email me a code'}
              </Button>
            </form>
          </>
        )}
      </div>
    </BaseModal>
  );
};

export default LoginModal;
